import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';
import { unmaskNumber } from '@givebutter/util';
import * as Form from 'components/form';
import { twMerge } from 'tailwind-merge';
import { Bidder } from 'types/domain';

interface IBidderFormContext {
    bidder: Bidder;
}

const BidderFormContext = createContext<IBidderFormContext | undefined>(undefined);

const useBidderFormContext = () => {
    const context = useContext(BidderFormContext);
    if (!context) {
        throw new Error('useBidderFormContext must be used within a BidderFormProvider');
    }
    return context;
};

interface BidderFormProviderProps {
    bidder: Bidder;
    children: ReactNode;
}

const BidderFormProvider = ({ bidder, children }: BidderFormProviderProps) => {
    const value = useMemo(() => ({ bidder }), [bidder]);

    return <BidderFormContext.Provider value={value}>{children}</BidderFormContext.Provider>;
};

const BidderFormStartingNumberReadOnly = () => {
    const { bidder } = useBidderFormContext();
    const bidderStartingNumber = bidder?.number;

    const DisplayValue = useCallback(() => {
        return (
            <p
                className={twMerge(
                    'text-sm',
                    bidderStartingNumber ? 'text-gray-600' : 'text-gray-300',
                )}
            >
                {bidderStartingNumber || 'No answer'}
            </p>
        );
    }, [bidderStartingNumber]);

    return (
        <p className={twMerge('text-sm', bidderStartingNumber ? 'text-gray-600' : 'text-gray-300')}>
            <DisplayValue />
        </p>
    );
};

const BidderFormStartingNumber = () => {
    const { control, path } = Form.useGivebutterFormContext();

    return (
        <>
            <div className="flex max-w-28 flex-col">
                <Form.Label className="sr-only" name={path('manualBidderNumber')}>
                    Bidder Number
                </Form.Label>
                <Form.TextInputRoot
                    name={path('manualBidderNumber')}
                    control={control}
                    rules={{
                        required: 'Starting bidder number is required.',
                        validate: {
                            greaterThanZero: value =>
                                unmaskNumber(value) >= 1 ||
                                'The bidder number start must be at least 1.',
                        },
                    }}
                    inputRootProps={{
                        mask: 'whole-number',
                    }}
                >
                    <Form.TextInputForm />
                </Form.TextInputRoot>
            </div>
            <Form.Error name={path('manualBidderNumber')} />
        </>
    );
};

const Root = BidderFormProvider;
const StartingNumberReadOnly = BidderFormStartingNumberReadOnly;
const StartingNumber = BidderFormStartingNumber;

export {
    BidderFormContext,
    useBidderFormContext,
    //
    BidderFormProvider,
    BidderFormStartingNumberReadOnly,
    BidderFormStartingNumber,
    //
    Root,
    StartingNumberReadOnly,
    StartingNumber,
};
